import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ALL_CORRECTIVE_ACTION_STATUSES, type CorrectiveActionView } from '@lims/shared-types';
import { qualityApi } from '../../api/endpoints.js';
import {
  ActionButton,
  CheckboxField,
  DataTable,
  FilterBar,
  PageHeader,
  SelectField,
  StatusBadge,
  type DataColumn,
} from '../../components/ui/index.js';
import { formatDate } from '../../components/format.js';
import { currentLocale } from '../../i18n/index.js';
import { actionStatusTone } from './operations-ui.js';
import { RegisterPanel } from '../reports/register-panel.js';

/**
 * Corrective actions across the register (Phase 12, D165). "Mine" is the
 * signed-in person, resolved by the server from the session.
 */
export function CorrectiveActionsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const locale = currentLocale();
  const [status, setStatus] = useState<string | null>(null);
  const [mine, setMine] = useState(false);
  const [overdue, setOverdue] = useState(false);
  const [page, setPage] = useState(1);

  const actions = useQuery({
    queryKey: ['corrective-actions', { page, status, mine, overdue }],
    queryFn: () =>
      qualityApi.listActions({
        page,
        ...(status ? { status } : {}),
        ...(mine ? { mine: 'true' } : {}),
        ...(overdue ? { overdue: 'true' } : {}),
      }),
  });

  const columns: readonly DataColumn<CorrectiveActionView>[] = [
    {
      key: 'record',
      header: t('correctiveActions.record'),
      render: (row) => (
        <span className="text-default-900" dir="ltr">
          {row.qualityRecordNumber}
        </span>
      ),
    },
    { key: 'description', header: t('quality.actionDescription'), render: (row) => row.description },
    { key: 'responsible', header: t('quality.responsible'), render: (row) => row.responsibleUser.displayName ?? '' },
    { key: 'dueOn', header: t('quality.dueOn'), render: (row) => formatDate(row.dueOn, { locale }) },
    {
      key: 'status',
      header: t('common.status'),
      render: (row) => (
        <span className="flex flex-wrap gap-inline">
          <StatusBadge label={t(`correctiveActionStatus.${row.status}`)} tone={actionStatusTone(row.status)} />
          {row.isOverdue ? <StatusBadge label={t('quality.overdue')} tone="danger" /> : null}
        </span>
      ),
    },
    {
      key: 'actions',
      header: t('common.actions'),
      isHeaderHidden: true,
      align: 'end',
      render: (row) => (
        <ActionButton priority="tertiary" size="sm" onPress={() => navigate(`/quality/${row.qualityRecordId}`)}>
          {t('common.open')}
        </ActionButton>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={t('correctiveActions.title')}
        subtitle={t('correctiveActions.subtitle')}
        breadcrumbs={[{ label: t('nav.correctiveActions') }]}
      />
      <RegisterPanel
        toolbar={
          <FilterBar>
            <SelectField
              label={t('common.status')}
              value={status}
              onChange={(value) => {
                setStatus(value);
                setPage(1);
              }}
              placeholder={t('common.all')}
              options={ALL_CORRECTIVE_ACTION_STATUSES.map((value) => ({ value, label: t(`correctiveActionStatus.${value}`) }))}
            />
            <CheckboxField
              label={t('correctiveActions.mine')}
              value={mine}
              onChange={(value) => {
                setMine(value);
                setPage(1);
              }}
            />
            <CheckboxField
              label={t('correctiveActions.overdueOnly')}
              value={overdue}
              onChange={(value) => {
                setOverdue(value);
                setPage(1);
              }}
            />
          </FilterBar>
        }
      >
        <DataTable
          label={t('correctiveActions.title')}
          columns={columns}
          rows={actions.data?.data ?? []}
          rowKey={(row) => row.id}
          isLoading={actions.isLoading}
          error={actions.error}
          onRetry={() => void actions.refetch()}
          emptyContent={t('correctiveActions.noneYet')}
          page={page}
          totalPages={actions.data?.meta.totalPages ?? 1}
          onPageChange={setPage}
        />
      </RegisterPanel>
    </>
  );
}
